import { createSelector } from 'reselect'

import { Nullable, NullableString } from '@probatix/common/models/global'

import { Customer } from '@probatix/common/services/RTKServices/patients/index.model'
import { Product } from '@probatix/common/services/RTKServices/checkoutSteps/checkoutStepsService.model'
import { Location } from '@probatix/common/services/RTKServices/locations/index.model'
import { GeoJsonProperties } from 'geojson'

import { FlowType, TestsFormServiceState, TestsServiceRootState } from '../testsService.model'

export const selectTestsFormState = (state: TestsServiceRootState): TestsFormServiceState => state.tests.form

export const selectTestsFormLocation = createSelector(
  [selectTestsFormState],
  (state): Nullable<Location> => state.location,
)

export const selectTestsFormBaseProduct = createSelector(
  [selectTestsFormState],
  (state): Nullable<Product> => state.baseProduct,
)

export const selectTestsFormBaseProductName = createSelector(
  [selectTestsFormState],
  (state): NullableString => state.product?.baseProductName,
)

export const selectTestsFormProduct = createSelector(
  [selectTestsFormState],
  (state): Nullable<Product> => state.product,
)

export const selectTestsFormStore = createSelector(
  [selectTestsFormState],
  (state): Nullable<GeoJsonProperties> => state.store,
)

export const selectTestsFormPerson = createSelector(
  [selectTestsFormState],
  (state): Nullable<Customer> => state.person,
)

export const selectTestsFormPersonEmail = createSelector(
  [selectTestsFormState],
  (state): string => state!.person?.email!,
)

export const selectTestsFormTime = createSelector(
  [selectTestsFormState],
  (state): Nullable<Date> => state.time,
)

export const selectTestsFlow = createSelector(
  [selectTestsFormState],
  (state): FlowType => state.flow,
)

export const selectTestsCrossOutPrice = createSelector(
  [selectTestsFormState],
  (state): boolean => state.crossOutPrice,
)

export const selectTestsCheckedProduct = createSelector(
  [selectTestsFormState],
  (state): boolean => state.checkedProduct,
)

export const selectStoreBaseUrl = createSelector(
  [selectTestsFormStore],
  (store): string => {
    if (!store?.uuid) {
      return ''
    }

    return store.properties.instanceBaseUrl!
  },
)

export const selectLocationCode = createSelector(
  [selectTestsFormLocation],
  (location): string => {
    if (!location?.locationCode) {
      return ''
    }

    return location.locationCode!
  },
)

export const selectIsIdRequiredByForm = createSelector(
  [selectTestsFormProduct],
  (product): boolean => {
    if (!product?.uuid) {
      return false
    }

    return product.isPersonalIdRequired!
  },
)

export const selectIsHealthInsurancePolicyNumberRequiredByForm = createSelector(
  [selectTestsFormProduct],
  (product): boolean => {
    if (!product?.uuid) {
      return false
    }

    return product.healthInsurancePolicyNumberRequired!
  },
)

export const selectIsHealthInsuranceCompanyNumberRequiredByForm = createSelector(
  [selectTestsFormProduct],
  (product): boolean => {
    if (!product?.uuid) {
      return false
    }

    return product.healthInsuranceCompanyNumberRequired
  },
)

export const selectIsAddressRequiredByForm = createSelector(
  [selectTestsFormProduct],
  (product): boolean => {
    if (!product?.uuid) {
      return false
    }

    return product.addressRequired!
  },
)

export const selectIsLocationCodeByLocation = createSelector(
  [selectTestsFormStore],
  (location): string => {
    if (!location?.locationCode) {
      return ''
    }

    return location.locationCode!
  },
)

export const selectIsUUIDByProduct = createSelector(
  [selectTestsFormProduct],
  (product): string => {
    if (!product?.uuid) {
      return ''
    }

    return product.uuid!
  },
)

export const selectIsNameByForm = createSelector(
  [selectTestsFormStore],
  (location): string => {
    if (!location?.uuid) {
      return ''
    }

    return location!.name!
  },
)

export const selectAppointmentCheckTimeByForm = createSelector(
  [selectTestsFormTime],
  (time): boolean => {
    if (!time) {
      return false
    }

    return true
  },
)

export const selectAppointmentTimeByForm = createSelector(
  [selectTestsFormTime],
  (time): Date => {
    if (!time) {
      return new Date()
    }

    return time
  },
)

export const selectIsPhoneNumberRequiredByForm = createSelector(
  [selectTestsFormProduct],
  (product): boolean => {
    if (!product?.uuid) {
      return false
    }

    return product.phoneNumberRequired!
  },
)

export const selectFlow = createSelector(
  [selectTestsFlow],
  (flow): FlowType => {
    if (!flow) {
      return FlowType.FULL
    }

    return flow
  },
)

export const selectMinimumAgeByForm = createSelector(
  [selectTestsFormProduct],
  (product): number => {
    if (!product?.uuid) {
      return 0
    }

    return product.minimumAge!
  },
)
